/**
 * Chemistry Disambiguation — Theme F (Bill Kinney walkthrough 2026-05-14).
 *
 * Detects ambiguous chemistry reference terms in a user's natural-language
 * message ("alpha hydrogen", "ortho", "the ring", "the amine", "the carbon")
 * and enumerates the candidate interpretations on a specific compound.
 *
 * Routing flow (see `proto/src/gibbs/text-to-action/index.js` for integration):
 *
 *   1. User message arrives.
 *   2. After the deterministic shortcut chain has had its pass, but before
 *      LLM dispatch, `detectChemistryAmbiguity` runs.
 *   3. If 2+ candidates exist on the loaded compound, a clarify intent with
 *      `cascade: 'chemistry_disambiguation'` is emitted.
 *   4. The user picks an option in chat. `resumeFromClarification.js`
 *      rewrites the original message with the disambiguated phrasing
 *      (e.g., "alpha hydrogen" → "alpha-to-amine hydrogen") and re-runs the
 *      dispatch chain.
 *
 * **v1 LIMITATION — SMILES substring heuristics, not RDKit substructure
 * matching.** The candidate-presence checks below use string-level pattern
 * matching against the compound's SMILES. That's enough to answer the
 * critical question "does this functional group appear at all?" for the
 * common cases Bill flagged, but it can miss:
 *   - SMILES variants with different ring-closure numbering or atom order
 *   - Aromatic-aliphatic distinction (some terms only apply to one)
 *   - Multiple instances of the same group (e.g., two amines — the rule
 *     currently treats them as "one amine candidate"; v2 enumerates each)
 *
 * **Path to v2**: replace each rule's `detectFn` with an RDKit-WASM
 * substructure match using `rdkitLoader.js`, returning the actual matched
 * atom indices so the clarify UI can highlight each option on the 2D
 * structure (composes with Theme A — mitigation visualization).
 *
 * @module gibbs/knowledge/chemistryDisambiguation
 */

// ─── Functional-group SMILES heuristics (v1) ───────────────────────────────
//
// Each detector takes a SMILES string and returns true if the functional
// group is present. The patterns are conservative — they err on the side of
// "yes, present" so the user gets a clarification rather than a wrong
// action. False positives lead to extra clicks; false negatives lead to
// wrong-interpretation execution.

function hasAmine(smiles) {
    if (!smiles) return false;
    // Aliphatic N (not aromatic n; not nitro N(=O)=O; not nitrile C#N).
    // Strip nitro / nitrile patterns first so the bare-N check below is
    // not fooled by them.
    const s = smiles
        .replace(/N\(=O\)=O/g, '')
        .replace(/\[N\+\]\(=O\)\[O-\]/g, '')
        .replace(/N#/g, '');
    return /[A-Za-z\)\]]N(?![a-z])(?!=)/.test(s) || /^N(?![a-z])/.test(s);
}

function hasCarbonyl(smiles) {
    if (!smiles) return false;
    // C=O in any context (ketone, aldehyde, ester, amide carbonyl).
    return /C\(=O\)|C=O/.test(smiles);
}

function hasSulfonyl(smiles) {
    if (!smiles) return false;
    // S(=O)(=O)- pattern (sulfone, sulfonamide, sulfonate).
    return /S\(=O\)\(=O\)/.test(smiles);
}

function hasCarboxylate(smiles) {
    if (!smiles) return false;
    // C(=O)O[H?] or C(=O)[O-]. Distinct from amide / ester for "alpha"
    // disambiguation purposes only when the user asks; otherwise rolled
    // into carbonyl.
    return /C\(=O\)O(?![A-Za-z])|C\(=O\)\[O-\]/.test(smiles);
}

function countAromaticRings(smiles) {
    if (!smiles) return 0;
    // Ring closure digits come in pairs; lowercase aromatic atoms indicate
    // an aromatic ring. This is a very rough count — fine for the
    // "0 / 1 / 2+" decision the disambiguator needs, not for atom-level
    // analysis.
    const aromaticAtoms = (smiles.match(/[cnops]/g) || []).length;
    if (aromaticAtoms < 4) return 0;
    // Each aromatic ring uses one closure digit. Count distinct ring-
    // closure digits that appear with adjacent aromatic atoms.
    const closures = new Set();
    for (let i = 0; i < smiles.length; i += 1) {
        const ch = smiles[i];
        if (/[1-9]/.test(ch)) {
            const prev = smiles[i - 1] || '';
            if (/[cnops]/.test(prev)) closures.add(ch);
        }
    }
    return closures.size;
}

function countAliphaticRings(smiles) {
    if (!smiles) return 0;
    const closures = new Set();
    for (let i = 0; i < smiles.length; i += 1) {
        const ch = smiles[i];
        if (/[1-9]/.test(ch)) {
            const prev = smiles[i - 1] || '';
            if (/[A-Z]/.test(prev)) closures.add(ch);
        }
    }
    return closures.size;
}

function totalRingCount(smiles) {
    return countAromaticRings(smiles) + countAliphaticRings(smiles);
}

function countAmines(smiles) {
    if (!smiles) return 0;
    const stripped = smiles
        .replace(/N\(=O\)=O/g, '')
        .replace(/\[N\+\]\(=O\)\[O-\]/g, '')
        .replace(/N#/g, '');
    const matches = stripped.match(/[A-Za-z\)\]]N(?![a-z])(?!=)|^N(?![a-z])/g) || [];
    return matches.length;
}

function countCarbons(smiles) {
    if (!smiles) return 0;
    // Uppercase C (aliphatic) + lowercase c (aromatic), minus Cl / Ca / Cr
    // false positives. Coarse — only needed to distinguish "one carbon"
    // from "many".
    const stripped = smiles.replace(/Cl|Ca|Cr|Cs|Co|Cu/g, '');
    return (stripped.match(/[Cc]/g) || []).length;
}

// ─── Ambiguity-rule catalog ────────────────────────────────────────────────

/**
 * Each rule:
 *   - `id`          — short identifier
 *   - `term`        — regex matching the ambiguous phrase in user text
 *   - `question`    — clarification prompt shown to the user
 *   - `candidates`  — array of {id, label, rewrite, detectFn(smiles)}
 *                     where `rewrite` is the disambiguated phrase to inject
 *                     back into the user message on resume
 *   - `rationale`   — comment explaining the linguistic precedent
 *
 * A candidate is "applicable" when its `detectFn` returns true for the
 * compound. The rule fires a clarification when 2+ candidates are applicable.
 *
 * **Catalog discipline**: every entry has a `rationale` field documenting
 * why this term is ambiguous and the source (Bill walkthrough, common
 * medchem usage, etc.). Resist ad-hoc additions — every new rule needs an
 * SME-traceable reason.
 */
export const AMBIGUITY_RULES = [
    {
        id: 'alpha',
        term: /\balpha[\s-]+(hydrogen|h|position|carbon|c|atom)\b/i,
        question: 'Alpha to which group?',
        rationale: 'Bill Kinney walkthrough 2026-05-14: "alpha" requires '
            + 'a reference functional group. Common references are amine, '
            + 'carbonyl, sulfonyl, carboxylate.',
        candidates: [
            {
                id: 'amine',
                label: 'Alpha to the amine',
                rewrite: 'alpha-to-amine',
                detectFn: hasAmine,
            },
            {
                id: 'carbonyl',
                label: 'Alpha to the carbonyl',
                rewrite: 'alpha-to-carbonyl',
                detectFn: hasCarbonyl,
            },
            {
                id: 'carboxylate',
                label: 'Alpha to the carboxylate',
                rewrite: 'alpha-to-carboxylate',
                detectFn: hasCarboxylate,
            },
            {
                id: 'sulfonyl',
                label: 'Alpha to the sulfonyl',
                rewrite: 'alpha-to-sulfonyl',
                detectFn: hasSulfonyl,
            },
        ],
    },
    {
        id: 'ortho',
        term: /\bortho\b(?!\s+to\s+the)/i,
        question: 'Ortho to which substituent?',
        rationale: 'Bill walkthrough implicit: "ortho" presupposes a reference '
            + 'substituent on an aromatic ring. When the compound has multiple '
            + 'ring substituents, "ortho" alone is ambiguous.',
        candidates: [
            // The ortho rule is the trickiest — without a real graph
            // analysis we can't enumerate the specific substituent atoms.
            // v1 emits a generic two-option clarify when there are any
            // ring substituents at all; v2 (RDKit) enumerates the actual
            // substituent labels.
            {
                id: 'first_substituent',
                label: 'Ortho to the first ring substituent',
                rewrite: 'ortho-to-first-substituent',
                detectFn: (smiles) => countAromaticRings(smiles) >= 1,
            },
            {
                id: 'second_substituent',
                label: 'Ortho to the second ring substituent',
                rewrite: 'ortho-to-second-substituent',
                // Heuristic: a second substituent shows up as a branch on
                // the aromatic ring — at least one '(' immediately after
                // a lowercase ring atom.
                detectFn: (smiles) => /[cnops]\(/.test(smiles || ''),
            },
        ],
    },
    {
        id: 'the_ring',
        term: /\bthe\s+ring\b/i,
        question: 'Which ring?',
        rationale: 'Common in polycyclic compounds: "the ring" is unambiguous '
            + 'only when there is a single ring system. Multi-ring compounds '
            + 'require disambiguation between aromatic / aliphatic / fused.',
        candidates: [
            {
                id: 'aromatic_ring',
                label: 'The aromatic ring',
                rewrite: 'the aromatic ring',
                detectFn: (smiles) => countAromaticRings(smiles) >= 1,
            },
            {
                id: 'aliphatic_ring',
                label: 'The aliphatic / saturated ring',
                rewrite: 'the aliphatic ring',
                detectFn: (smiles) => countAliphaticRings(smiles) >= 1,
            },
            {
                id: 'second_aromatic_ring',
                label: 'The second aromatic ring',
                rewrite: 'the second aromatic ring',
                detectFn: (smiles) => countAromaticRings(smiles) >= 2,
            },
        ],
    },
    {
        id: 'the_amine',
        term: /\bthe\s+amine\b/i,
        question: 'Which amine?',
        rationale: 'When a compound carries two or more amine groups, "the '
            + 'amine" is ambiguous. Common in amino acids, piperazines, '
            + 'sulfonamide-anilines.',
        candidates: [
            // Without graph analysis we cannot label each amine by its
            // chemical context (primary vs secondary; aliphatic vs
            // aromatic). v1 shows numbered options if ≥ 2 amines are
            // present; v2 labels by sidechain / ring context.
            {
                id: 'first_amine',
                label: 'The first amine',
                rewrite: 'the first amine',
                detectFn: (smiles) => countAmines(smiles) >= 1,
            },
            {
                id: 'second_amine',
                label: 'The second amine',
                rewrite: 'the second amine',
                detectFn: (smiles) => countAmines(smiles) >= 2,
            },
        ],
    },
    {
        id: 'the_carbon',
        term: /\bthe\s+carbon\b/i,
        question: 'Which carbon? Compounds usually have many — give a '
            + 'reference (alpha to which group, on which ring, etc.)',
        rationale: 'A bare "the carbon" reference is almost never sufficient '
            + 'on a multi-carbon compound. Surfacing the question to the user '
            + 'is more reliable than guessing.',
        candidates: [
            {
                id: 'alpha_carbon',
                label: 'An alpha carbon (next to a functional group)',
                rewrite: 'an alpha carbon',
                detectFn: (smiles) => countCarbons(smiles) >= 2,
            },
            {
                id: 'aromatic_carbon',
                label: 'An aromatic ring carbon',
                rewrite: 'an aromatic ring carbon',
                detectFn: (smiles) => countAromaticRings(smiles) >= 1,
            },
            {
                id: 'aliphatic_carbon',
                label: 'An aliphatic chain carbon',
                rewrite: 'an aliphatic chain carbon',
                detectFn: (smiles) => /CC|C\(/.test(smiles || ''),
            },
        ],
    },
];

// ─── Detector ──────────────────────────────────────────────────────────────

/**
 * Detect chemistry-reference ambiguities in a user message against a given
 * compound. Returns the FIRST ambiguous rule found (or null) so the chain
 * only emits one clarification per turn — multi-ambiguity messages
 * resolve incrementally.
 *
 * Behavior matrix per the brief's acceptance criteria:
 *   - 2+ candidates applicable → return { rule, candidates }       (clarify)
 *   - 1 candidate applicable   → return null                       (pass through)
 *   - 0 candidates applicable  → return { rule, candidates: [] }   (chat: "no clear X here")
 *
 * @param {string} userText - The user's natural-language message
 * @param {string} smiles - The active compound's SMILES (may be absent)
 * @returns {null | { rule: object, candidates: Array<object>, noMatch?: boolean }}
 */
export function detectChemistryAmbiguity(userText, smiles) {
    if (!userText || typeof userText !== 'string') return null;

    for (const rule of AMBIGUITY_RULES) {
        if (!rule.term.test(userText)) continue;

        // If no compound context, we cannot enumerate candidates — skip
        // silently. The downstream chain can still handle the request via
        // LLM tier. The detector's job is to catch *compound-grounded*
        // ambiguities, not generic chemistry questions.
        if (!smiles || typeof smiles !== 'string') return null;

        const applicable = rule.candidates.filter((c) => {
            try {
                return c.detectFn(smiles);
            } catch (err) {
                // Detector functions are heuristic and should never throw;
                // if one does, treat as non-applicable rather than
                // crashing the chain.
                // eslint-disable-next-line no-console
                console.warn(
                    `[chemistryDisambiguation] detectFn threw for `
                    + `${rule.id}/${c.id}:`, err?.message
                );
                return false;
            }
        });

        if (applicable.length === 0) {
            // 0-match case: rule term appeared but no candidate fits the
            // compound. Surface as a chat hint rather than a silent fall
            // through.
            return { rule, candidates: [], noMatch: true };
        }

        if (applicable.length === 1) {
            // Unambiguous on this compound — no clarification needed; the
            // single interpretation is implicit. Caller proceeds.
            return null;
        }

        return { rule, candidates: applicable };
    }

    return null;
}

// ─── Clarification builder ─────────────────────────────────────────────────

/**
 * Build a clarify intent suitable for the existing clarification cascade
 * (`text-to-action/clarification.js` shape).
 *
 * The returned object plugs into the chain dispatch so that ChatBot.jsx
 * renders the standard clarify card and the user's option choice flows
 * back through `resumeFromClarification.js`.
 *
 * @param {{ rule: object, candidates: Array<object>, noMatch?: boolean }} detection
 * @param {string} originalMessage - The user's verbatim original message,
 *   stored on the cascade context so resume can rewrite + redispatch it
 * @returns {object} clarify intent
 */
export function buildChemistryClarification(detection, originalMessage) {
    if (!detection || !detection.rule) return null;
    const { rule, candidates, noMatch } = detection;

    if (noMatch) {
        // No applicable candidate on this compound — return a chat-style
        // intent instead of a clarify. The caller surfaces this as a bot
        // message ("no clear alpha here…") and does NOT dispatch any
        // action.
        return {
            intent: 'chat',
            message: `I don't see a clear "${describeTerm(rule.id)}" on the active `
                + `compound. Could you point me at a specific atom or rephrase?`,
        };
    }

    const options = candidates.map((c) => ({
        label: c.label,
        value: {
            cascade: 'chemistry_disambiguation',
            ruleId: rule.id,
            candidateId: c.id,
            rewrite: c.rewrite,
        },
    }));

    return {
        intent: 'clarify',
        cascade: 'chemistry_disambiguation',
        question: rule.question,
        options,
        missing: [],
        context: {
            cascade: 'chemistry_disambiguation',
            ruleId: rule.id,
            originalMessage,
        },
    };
}

function describeTerm(ruleId) {
    switch (ruleId) {
        case 'alpha': return 'alpha position';
        case 'ortho': return 'ortho substituent';
        case 'the_ring': return 'ring';
        case 'the_amine': return 'amine';
        case 'the_carbon': return 'carbon';
        default: return ruleId;
    }
}

// ─── Resume rewriter ───────────────────────────────────────────────────────

/**
 * Given the cascade context + the user's picked candidate, rewrite the
 * original message with the disambiguated phrasing so the chain re-dispatch
 * sees an unambiguous request.
 *
 * Example:
 *   originalMessage: "add F to the alpha hydrogen"
 *   ruleId: 'alpha', picked rewrite: 'alpha-to-amine'
 *   rewritten:      "add F to the alpha-to-amine hydrogen"
 *
 * The rewrite preserves the rest of the message verbatim — only the
 * ambiguous term itself is replaced.
 *
 * @param {string} originalMessage
 * @param {string} ruleId
 * @param {string} rewrite - The chosen candidate's `rewrite` string
 * @returns {string}
 */
export function rewriteWithDisambiguation(originalMessage, ruleId, rewrite) {
    const rule = AMBIGUITY_RULES.find((r) => r.id === ruleId);
    if (!rule || !originalMessage) return originalMessage || '';
    // Replace the matched ambiguous span with the disambiguated rewrite.
    // The rule's `term` regex captures the ambiguous noun phrase; we
    // substitute the candidate's `rewrite` for the leading word and keep
    // any trailing modifier ("hydrogen", "position", etc.) intact.
    return originalMessage.replace(rule.term, (full, modifier) => {
        if (rule.id === 'alpha' && modifier) {
            return `${rewrite} ${modifier}`;
        }
        return rewrite;
    });
}
